/**
 * AI Module — RTK Query API Service
 *
 * Single source of truth for all AI module API calls.
 * Auto-generates React hooks for each endpoint.
 * Scoped entirely to the AI module; the rest of the app is untouched.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_GENAI_V1_SERVER_URL } from 'js/constants/api';
import { getAccessToken } from 'js/reducers/session/selectors';
import type {
  ApiTool,
  ApiToolset,
  ApiToolsetTool,
  ApiToolsetExport,
  ApiMCPConfig,
  ApiToolsetChangelog,
  ApiExtMcpServer,
  ApiGatewayTool,
  ApiGatewayToolSearchResult,
  CreateToolsetFromNexsetsPayload,
  CreateToolsetPayload,
  CreateToolFromNexsetPayload,
  CreateToolFromNexsetResponse,
  SyncToolResponse,
  SyncToolsetResponse,
  UpdateToolsetPayload,
  UpdateIndividualToolPayload,
  CloneToolsetPayload,
  ToolsUpdatePayload,
  CreateToolsetExportPayload,
  ToolsetsQueryParams,
  ToolsQueryParams,
  CreateExtMcpServerPayload,
  UpdateExtMcpServerPayload,
  GatewayToolsQueryParams,
  GatewaySemanticSearchPayload,
  RemoveToolsFromToolsetResponse,
} from './aiApiTypes';

export const aiApi = createApi({
  reducerPath: 'aiApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_GENAI_V1_SERVER_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getAccessToken(getState() as any);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  // Prevent aggressive refetching — cache data for 5 minutes
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  tagTypes: [
    'Toolset',
    'ToolsetList',
    'ToolsetChangelog',
    'Export',
    'GatewayServer',
    'GatewayTool',
    'Tool',
    'ToolList',
  ],
  endpoints: (builder) => ({
    // ---- Toolsets ----

    getToolsets: builder.query<ApiToolset[], ToolsetsQueryParams | void>({
      query: (params) => ({
        url: '/toolsets',
        params: params ?? { per_page: 50 },
      }),
      providesTags: ['ToolsetList'],
    }),

    getToolset: builder.query<ApiToolset, number | string>({
      query: (id) => `/toolsets/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'Toolset', id: String(id) }],
    }),

    createToolsetFromNexsets: builder.mutation<ApiToolset, CreateToolsetFromNexsetsPayload>({
      query: (body) => ({
        url: '/toolsets:from_nexsets',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ToolsetList'],
    }),

    createToolset: builder.mutation<ApiToolset, CreateToolsetPayload>({
      query: (body) => ({
        url: '/toolsets',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ToolsetList'],
    }),

    updateToolset: builder.mutation<
      ApiToolset,
      { id: number | string; data: UpdateToolsetPayload }
    >({
      query: ({ id, data }) => ({
        url: `/toolsets/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Toolset', id: String(id) }, 'ToolsetList'],
    }),

    activateToolset: builder.mutation<{ tool_set_id: number; status: string }, number | string>({
      query: (id) => ({
        url: `/toolsets/${id}:activate`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Toolset', id: String(id) }, 'ToolsetList'],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            aiApi.util.updateQueryData('getToolsets', undefined, (draft) => {
              const idx = draft.findIndex((ts) => ts.tool_set_id === data.tool_set_id);
              if (idx >= 0) draft[idx] = { ...draft[idx], status: data.status };
            }),
          );
        } catch {
          /* refetch on next mount */
        }
      },
    }),

    pauseToolset: builder.mutation<{ tool_set_id: number; status: string }, number | string>({
      query: (id) => ({
        url: `/toolsets/${id}:pause`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Toolset', id: String(id) }, 'ToolsetList'],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            aiApi.util.updateQueryData('getToolsets', undefined, (draft) => {
              const idx = draft.findIndex((ts) => ts.tool_set_id === data.tool_set_id);
              if (idx >= 0) draft[idx] = { ...draft[idx], status: data.status };
            }),
          );
        } catch {
          /* refetch on next mount */
        }
      },
    }),

    deleteToolset: builder.mutation<{ deleted: boolean }, number | string>({
      query: (id) => ({
        url: `/toolsets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Toolset', id: String(id) }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            aiApi.util.updateQueryData('getToolsets', undefined, (draft) => {
              const idx = draft.findIndex((ts) => String(ts.tool_set_id) === String(id));
              if (idx >= 0) draft.splice(idx, 1);
            }),
          );
        } catch {
          /* error handled by component */
        }
      },
    }),

    cloneToolset: builder.mutation<ApiToolset, { id: number | string; data: CloneToolsetPayload }>({
      query: ({ id, data }) => ({
        url: `/toolsets/${id}:clone`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ToolsetList'],
    }),

    // ---- Toolset Tools ----

    getToolsetTools: builder.query<ApiToolsetTool[], number | string>({
      query: (id) => `/toolsets/${id}/tools`,
      providesTags: (_r, _e, id) => [{ type: 'Toolset', id: `${id}-tools` }],
    }),

    addToolsToToolset: builder.mutation<
      ApiToolset,
      { toolsetId: number | string; data: ToolsUpdatePayload }
    >({
      query: ({ toolsetId, data }) => ({
        url: `/toolsets/${toolsetId}/tools`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, { toolsetId }) => [
        { type: 'Toolset', id: `${toolsetId}-tools` },
        { type: 'Toolset', id: String(toolsetId) },
        'ToolsetList',
      ],
    }),

    removeToolsFromToolset: builder.mutation<
      RemoveToolsFromToolsetResponse,
      { toolsetId: number | string; data: ToolsUpdatePayload }
    >({
      query: ({ toolsetId, data }) => ({
        url: `/toolsets/${toolsetId}/tools`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: (_r, _e, { toolsetId }) => [
        { type: 'Toolset', id: `${toolsetId}-tools` },
        { type: 'Toolset', id: String(toolsetId) },
        'ToolsetList',
      ],
    }),

    createToolFromNexset: builder.mutation<
      CreateToolFromNexsetResponse,
      CreateToolFromNexsetPayload
    >({
      query: (body) => ({
        url: '/tools:from_nexset',
        method: 'POST',
        body,
      }),
      // Refetch the list rather than upserting: the response carries only ids
      // and a version status, not a full tool row, so there is nothing to merge
      // into the cache. Invalidation must live here and not in the caller —
      // `publish: true` returns an already-active tool, so the follow-up
      // `:activate` (whose onQueryStarted used to do this) is correctly skipped.
      invalidatesTags: ['ToolList'],
    }),

    // ---- Standalone Tools ----

    getTools: builder.query<ApiTool[], ToolsQueryParams | void>({
      query: (params) => ({
        url: '/tools',
        params: params ?? { limit: 50 },
      }),
      providesTags: ['ToolList'],
    }),

    getTool: builder.query<ApiTool, number | string>({
      query: (id) => `/tools/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Tool', id: String(id) }],
    }),

    updateIndividualTool: builder.mutation<
      ApiTool,
      { id: number | string; data: UpdateIndividualToolPayload }
    >({
      query: ({ id, data }) => ({
        url: `/tools/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Tool', id: String(id) }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: tool } = await queryFulfilled;
          dispatch(
            aiApi.util.updateQueryData('getTools', undefined, (draft) => {
              const idx = draft.findIndex((t) => t.id === tool.id);
              if (idx >= 0) draft[idx] = tool;
            }),
          );
        } catch {
          /* refetch on next mount */
        }
      },
    }),

    activateTool: builder.mutation<ApiTool, number | string>({
      query: (id) => ({
        url: `/tools/${id}:activate`,
        method: 'POST',
      }),
      // 'ToolList' as well as the row: lifecycle is authorized server-side
      // (creator, or OPERATOR on the nexset), so the list must be re-read rather
      // than inferred from the response. The optimistic patch below still
      // updates the row immediately; the refetch confirms it.
      invalidatesTags: (_r, _e, id) => [{ type: 'Tool', id: String(id) }, 'ToolList'],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data: tool } = await queryFulfilled;
          // Upsert into the tools list — handles both existing tools
          // and newly created tools that fall outside the paginated window
          dispatch(
            aiApi.util.updateQueryData('getTools', undefined, (draft) => {
              const idx = draft.findIndex((t) => t.id === tool.id);
              if (idx >= 0) {
                draft[idx] = tool;
              } else {
                draft.unshift(tool);
              }
            }),
          );
        } catch {
          /* refetch on next mount */
        }
      },
    }),

    pauseTool: builder.mutation<ApiTool, number | string>({
      query: (id) => ({
        url: `/tools/${id}:pause`,
        method: 'POST',
      }),
      // 'ToolList' as well as the row: lifecycle is authorized server-side
      // (creator, or OPERATOR on the nexset), so the list must be re-read rather
      // than inferred from the response. The optimistic patch below still
      // updates the row immediately; the refetch confirms it.
      invalidatesTags: (_r, _e, id) => [{ type: 'Tool', id: String(id) }, 'ToolList'],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const { data: tool } = await queryFulfilled;
          dispatch(
            aiApi.util.updateQueryData('getTools', undefined, (draft) => {
              const idx = draft.findIndex((t) => t.id === tool.id);
              if (idx >= 0) draft[idx] = tool;
            }),
          );
        } catch {
          /* refetch on next mount */
        }
      },
    }),

    deleteTool: builder.mutation<{ deleted: boolean }, number | string>({
      query: (id) => ({
        url: `/tools/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        // Optimistic removal from list
        const patchResult = dispatch(
          aiApi.util.updateQueryData('getTools', undefined, (draft) => {
            const idx = draft.findIndex((t) => String(t.id) === String(id));
            if (idx >= 0) draft.splice(idx, 1);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['ToolList'],
    }),

    syncTool: builder.mutation<SyncToolResponse, { toolId: number; nexset_id: number }>({
      query: ({ nexset_id }) => ({
        url: '/tools:from_nexset',
        method: 'POST',
        body: { nexset_id, publish: false },
      }),
      invalidatesTags: (_r, _e, { toolId }) => [
        { type: 'Tool', id: String(toolId) },
        'ToolList',
        'ToolsetList',
      ],
    }),

    syncToolset: builder.mutation<SyncToolsetResponse, number | string>({
      query: (toolsetId) => ({
        url: `/toolsets/${toolsetId}:sync`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, toolsetId) => [
        { type: 'Toolset', id: String(toolsetId) },
        { type: 'Toolset', id: `${toolsetId}-tools` },
        'ToolsetList',
        'ToolList',
        { type: 'ToolsetChangelog', id: String(toolsetId) },
      ],
    }),

    // ---- Changelogs ----

    getToolsetChangelogs: builder.query<
      ApiToolsetChangelog[],
      { id: number | string; limit?: number }
    >({
      query: ({ id, limit }) => ({
        url: `/toolsets/${id}/changelogs`,
        params: { limit: limit ?? 50 },
      }),
      transformResponse: (response: any): ApiToolsetChangelog[] => {
        const items = response?.result?.items || response?.items || response || [];
        return Array.isArray(items) ? items : [];
      },
      providesTags: (_r, _e, { id }) => [{ type: 'ToolsetChangelog', id: String(id) }],
    }),

    // ---- MCP Config ----

    getToolsetMCPConfig: builder.query<ApiMCPConfig, number | string>({
      query: (id) => `/toolsets/${id}/mcp_config`,
    }),

    // ---- Exports ----

    createToolsetExport: builder.mutation<
      ApiToolsetExport,
      { toolsetId: number | string; data: CreateToolsetExportPayload }
    >({
      query: ({ toolsetId, data }) => ({
        url: `/toolsets/${toolsetId}/exports`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, { toolsetId }) => [
        { type: 'Toolset', id: String(toolsetId) },
        'ToolsetList',
      ],
    }),

    deleteExport: builder.mutation<{ deleted: boolean }, number | string>({
      query: (exportId) => ({
        url: `/exports/${exportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ToolsetList'],
    }),

    activateExport: builder.mutation<ApiToolsetExport, number | string>({
      query: (exportId) => ({
        url: `/exports/${exportId}:activate`,
        method: 'POST',
      }),
      invalidatesTags: ['ToolsetList'],
    }),

    pauseExport: builder.mutation<ApiToolsetExport, number | string>({
      query: (exportId) => ({
        url: `/exports/${exportId}:pause`,
        method: 'POST',
      }),
      invalidatesTags: ['ToolsetList'],
    }),

    // ---- MCP Gateway — External MCP Servers ----

    listExtMcpServers: builder.query<
      ApiExtMcpServer[],
      { toolsetId: number | string; status?: string }
    >({
      query: ({ toolsetId, status }) => ({
        url: `/toolsets/${toolsetId}/gateway/mcp-servers`,
        params: status ? { status } : undefined,
      }),
      transformResponse: (response: { items: ApiExtMcpServer[] }) => response.items,
      providesTags: (_r, _e, { toolsetId }) => [{ type: 'GatewayServer', id: String(toolsetId) }],
    }),

    createExtMcpServer: builder.mutation<
      ApiExtMcpServer,
      { toolsetId: number | string; data: CreateExtMcpServerPayload }
    >({
      query: ({ toolsetId, data }) => ({
        url: `/toolsets/${toolsetId}/gateway/mcp-servers`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_r, _e, { toolsetId }) => [
        { type: 'GatewayServer', id: String(toolsetId) },
      ],
    }),

    updateExtMcpServer: builder.mutation<
      ApiExtMcpServer,
      { toolsetId: number | string; serverId: number; data: UpdateExtMcpServerPayload }
    >({
      query: ({ toolsetId, serverId, data }) => ({
        url: `/toolsets/${toolsetId}/gateway/mcp-servers/${serverId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_r, _e, { toolsetId }) => [
        { type: 'GatewayServer', id: String(toolsetId) },
      ],
    }),

    deleteExtMcpServer: builder.mutation<
      { deleted: boolean },
      { toolsetId: number | string; serverId: number }
    >({
      query: ({ toolsetId, serverId }) => ({
        url: `/toolsets/${toolsetId}/gateway/mcp-servers/${serverId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { toolsetId }) => [
        { type: 'GatewayServer', id: String(toolsetId) },
        { type: 'GatewayTool', id: String(toolsetId) },
      ],
    }),

    syncExtMcpServer: builder.mutation<
      { job_id: string },
      { toolsetId: number | string; serverId: number }
    >({
      query: ({ toolsetId, serverId }) => ({
        url: `/toolsets/${toolsetId}/gateway/mcp-servers/${serverId}/sync`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { toolsetId }) => [{ type: 'GatewayTool', id: String(toolsetId) }],
    }),

    // ---- MCP Gateway — Tools Catalog ----

    listGatewayTools: builder.query<
      ApiGatewayTool[],
      { toolsetId: number | string } & GatewayToolsQueryParams
    >({
      query: ({ toolsetId, ...params }) => ({
        url: `/toolsets/${toolsetId}/gateway/tools`,
        params,
      }),
      transformResponse: (response: { items: ApiGatewayTool[] } | ApiGatewayTool[]) =>
        Array.isArray(response) ? response : response.items,
      providesTags: (_r, _e, { toolsetId }) => [{ type: 'GatewayTool', id: String(toolsetId) }],
    }),

    semanticSearchGatewayTools: builder.mutation<
      ApiGatewayToolSearchResult[],
      { toolsetId: number | string; data: GatewaySemanticSearchPayload }
    >({
      query: ({ toolsetId, data }) => ({
        url: `/toolsets/${toolsetId}/gateway/tools:search`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (
        response:
          | ApiGatewayToolSearchResult[]
          | { items: ApiGatewayToolSearchResult[] }
          | { results: ApiGatewayToolSearchResult[] },
      ) =>
        Array.isArray(response)
          ? response
          : 'items' in response
            ? response.items
            : ((response as any).results ?? []),
    }),
  }),
});

// Auto-generated hooks
export const {
  useGetToolsetsQuery,
  useGetToolsetQuery,
  useCreateToolsetFromNexsetsMutation,
  useCreateToolsetMutation,
  useUpdateToolsetMutation,
  useActivateToolsetMutation,
  usePauseToolsetMutation,
  useDeleteToolsetMutation,
  useCloneToolsetMutation,
  useGetToolsetToolsQuery,
  useAddToolsToToolsetMutation,
  useRemoveToolsFromToolsetMutation,
  useCreateToolFromNexsetMutation,
  useGetToolsQuery,
  useGetToolQuery,
  useUpdateIndividualToolMutation,
  useActivateToolMutation,
  usePauseToolMutation,
  useDeleteToolMutation,
  useSyncToolMutation,
  useSyncToolsetMutation,
  useGetToolsetChangelogsQuery,
  useGetToolsetMCPConfigQuery,
  useCreateToolsetExportMutation,
  useDeleteExportMutation,
  useActivateExportMutation,
  usePauseExportMutation,
  useListExtMcpServersQuery,
  useCreateExtMcpServerMutation,
  useUpdateExtMcpServerMutation,
  useDeleteExtMcpServerMutation,
  useSyncExtMcpServerMutation,
  useListGatewayToolsQuery,
  useSemanticSearchGatewayToolsMutation,
} = aiApi;
